import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpRight, Check, Hourglass, Lock } from 'lucide-react';

import { DiscordIcon, TelegramIcon, XLogoIcon } from '@/components/icons/BrandIcons';
import { Badge } from '@/components/ui/Badge';
import ConfettiBurst from '@/components/ui/Confetti';
import { GateShell } from '@/components/ui/GateShell';
import { Modal } from '@/components/ui/Modal';
import ConnectButton from '@/components/profile/ConnectButton';
import { useMissionSections } from '@/hooks/useMissionSections';
import { useClaimQuest } from '@/hooks/useClaimQuest';
import { useConnectTelegramBot } from '@/hooks/useConnectTelegramBot';
import { useProfile } from '@/hooks/useProfile';
import { getTelegramBotUsername } from '@/lib/api';
import { useUiStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { cn, formatPoints } from '@/lib/utils';
import type { Quest, QuestPrerequisite, QuestStatus, QuestType } from '@/lib/types';

import { QuestHero } from './QuestHero';

type GatedSocialProvider = 'telegram' | 'discord' | 'x';

const SOCIAL_GATE_COPY: Record<
  GatedSocialProvider,
  { label: string; Icon: typeof TelegramIcon }
> = {
  telegram: { label: 'Telegram', Icon: TelegramIcon },
  discord: { label: 'Discord', Icon: DiscordIcon },
  x: { label: 'X', Icon: XLogoIcon },
};

/**
 * Quests whose requirement is re-checked after the initial claim. Breaking
 * the condition can remove the points; repeated misses can reset standing.
 * The exact wording lives in `CONTINUOUS_CHECK_MESSAGE` and is the same for
 * every type so the modal stays consistent.
 */
const CONTINUOUS_CHECK_TYPES: ReadonlySet<QuestType> = new Set([
  'wallet_hold',
  'telegram_join',
  'discord_join',
  'twitter_follow',
]);

const CONTINUOUS_CHECK_MESSAGE =
  'Random re-checks happen after you claim. If the condition stops being met, the points can leave your score; repeated misses can reset your standing.';

function hasContinuousCheck(quest: Quest): boolean {
  return CONTINUOUS_CHECK_TYPES.has(quest.type);
}

const STATUS_LABEL: Record<QuestStatus, string> = {
  available: 'Available',
  in_progress: 'In progress',
  completed: 'Completed',
  locked: 'Locked',
  coming_soon: 'Coming soon',
};

/**
 * Returns the seconds remaining for a manual-verify cooldown.
 * `0` means ready; `null` means no cooldown ever applies.
 */
function useCooldownTimer(quest: Quest | undefined, ctaClickedAt: number | null) {
  const [now, setNow] = useState(() => Date.now());

  const needsTimer =
    !!quest &&
    quest.verifyMethod === 'manual' &&
    typeof quest.cooldownSeconds === 'number' &&
    quest.cooldownSeconds > 0 &&
    ctaClickedAt !== null;

  useEffect(() => {
    if (!needsTimer) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [needsTimer, quest?.id]);

  if (!quest) return { secondsLeft: null as number | null, hasStarted: false };
  if (quest.verifyMethod !== 'manual') return { secondsLeft: 0, hasStarted: true };
  if (!quest.cooldownSeconds || quest.cooldownSeconds <= 0)
    return { secondsLeft: 0, hasStarted: ctaClickedAt !== null };
  if (ctaClickedAt === null)
    return { secondsLeft: quest.cooldownSeconds, hasStarted: false };

  const elapsed = Math.floor((now - ctaClickedAt) / 1000);
  return {
    secondsLeft: Math.max(0, quest.cooldownSeconds - elapsed),
    hasStarted: true,
  };
}

interface SignInGateProps {
  title: string;
}

function SignInGate({ title }: SignInGateProps) {
  return (
    <GateShell
      icon={<Lock className="h-6 w-6" aria-hidden="true" strokeWidth={1.75} />}
      title="Connect to start"
      description={
        <>
          “{title}” needs a wallet. Connect to start the quest and put points on
          your board.
        </>
      }
    >
      <ConnectButton />
    </GateShell>
  );
}

interface SocialLinkGateProps {
  title: string;
  provider: GatedSocialProvider;
  onOpenProfile: () => void;
}

function SocialLinkGate({ title, provider, onOpenProfile }: SocialLinkGateProps) {
  const { label, Icon } = SOCIAL_GATE_COPY[provider];
  return (
    <GateShell
      icon={<Icon className="h-6 w-6" aria-hidden="true" strokeWidth={1.75} />}
      title={`Link your ${label} account`}
      titleId={`quest-modal-social-gate-${provider}`}
      description={
        <>
          “{title}” needs your {label} account linked before you can claim the
          points. Link it from your profile, then come back to finish the quest.
        </>
      }
    >
      <button type="button" onClick={onOpenProfile} className="btn-water">
        Open profile to link {label}
      </button>
    </GateShell>
  );
}

/**
 * Prerequisite checklist for a locked bonus quest: one row per required quest
 * with a live done/to-do state, a progress bar and an "N/M" counter. Replaces
 * the old comma-joined "Complete first: …" string with something scannable.
 */
function PrerequisiteChecklist({ items }: { items: QuestPrerequisite[] }) {
  const done = items.filter((p) => p.completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rounded-2xl border border-line bg-paper-warm/60 p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-wide text-slate2-500">
          <Lock className="h-3 w-3" aria-hidden="true" strokeWidth={2} />
          Unlock by completing
        </span>
        <span className="num-tabular text-xs font-semibold text-ink-900">
          {done}
          <span className="text-slate2-400">/{total}</span>
        </span>
      </div>

      <div
        className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-line"
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${done} of ${total} prerequisites complete`}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-water-500 to-forest-500 transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="space-y-1.5">
        {items.map((p) => (
          <li key={p.code} className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className={cn(
                'grid h-5 w-5 shrink-0 place-items-center rounded-full ring-1 ring-inset transition-colors',
                p.completed
                  ? 'bg-forest-500 text-white ring-forest-500'
                  : 'bg-white text-transparent ring-line',
              )}
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span
              className={cn(
                'text-sm',
                p.completed
                  ? 'text-slate2-400 line-through decoration-1'
                  : 'font-medium text-ink-900',
              )}
            >
              <span className="sr-only">{p.completed ? 'Completed: ' : 'To do: '}</span>
              {p.title}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Gate for a quest still locked by the onboarding sequence (see
 * `applyOnboardingSequence`). Only the synthesized bot card uses it — the
 * other quests show their normal locked view — since `TelegramBotConnectGate`
 * would otherwise let the user connect out of order.
 */
function SequenceLockedGate({ quest, onClose }: { quest: Quest; onClose: () => void }) {
  return (
    <GateShell
      icon={<Lock className="h-6 w-6" aria-hidden="true" strokeWidth={1.75} />}
      title={quest.title}
      titleId={`quest-modal-title-${quest.id}`}
      description={
        <>
          Complete “{quest.lockedBy}” first. Onboarding missions unlock one at a time, in
          order.
        </>
      }
    >
      <button type="button" onClick={onClose} className="btn-ghost">
        Got it
      </button>
    </GateShell>
  );
}

/**
 * Gate for the "Connect Telegram Bot" quest. Its data source is
 * bigwater_backend (rail2), not the `/v1/quests` claim endpoint, so it
 * bypasses `useClaimQuest`/`useCooldownTimer` entirely and drives its own
 * connect → deep-link → poll flow via `useConnectTelegramBot`. Rendered as a
 * `GateShell` (same family as `SignInGate`/`SocialLinkGate`) instead of the
 * hero/footer quest view, so it reads as the same kind of popup as "Join our
 * Telegram Community" / "Join our Discord Server".
 */
function TelegramBotConnectGate({ quest }: { quest: Quest }) {
  const qc = useQueryClient();
  const { connect, connecting, polling, error, refreshStatus, stopPolling, deepLink } =
    useConnectTelegramBot();
  const isCompleted = quest.status === 'completed';

  useEffect(() => {
    if (!isCompleted) return;
    stopPolling();
    // Points land in the main balance (bigwater_backend writes it directly),
    // which lives in the ['me']/['leaderboard'] queries — not the
    // ['participantProfile'] query the connect flow already invalidates.
    qc.invalidateQueries({ queryKey: ['me'] });
    qc.invalidateQueries({ queryKey: ['leaderboard'] });
  }, [isCompleted, stopPolling, qc]);

  if (isCompleted) {
    const botUsername = getTelegramBotUsername();
    return (
      <GateShell
        icon={<Check className="h-6 w-6" aria-hidden="true" strokeWidth={2} />}
        title="Telegram Bot connected"
        description={
          <>
            Your Telegram account is linked — +{formatPoints(quest.points)} points earned. Open
            the bot any time to submit tasks and get instant updates.
          </>
        }
      >
        {botUsername ? (
          <a
            href={`https://t.me/${botUsername}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-water"
          >
            Open Bot
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" strokeWidth={2.25} />
          </a>
        ) : null}
      </GateShell>
    );
  }

  return (
    <GateShell
      icon={<TelegramIcon className="h-6 w-6" aria-hidden="true" />}
      title={quest.title}
      description={quest.description}
    >
      <div className="flex flex-col items-center gap-2">
        {polling ? (
          <span className="text-xs text-slate2-500">Waiting for confirmation…</span>
        ) : null}
        {deepLink ? (
          <a href={deepLink} className="text-xs font-semibold text-water-600 hover:underline">
            Telegram didn’t open? Tap here
          </a>
        ) : null}
        {error ? <span className="text-xs text-rose2-600">{error.message}</span> : null}
        <div className="flex items-center justify-center gap-2">
          {polling ? (
            <button type="button" onClick={refreshStatus} className="btn-ghost">
              Refresh status
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => connect()}
            disabled={connecting}
            className="btn-water disabled:cursor-not-allowed disabled:opacity-50"
          >
            {connecting ? 'Connecting…' : (quest.ctaLabel ?? 'Connect Bot')}
          </button>
        </div>
      </div>
    </GateShell>
  );
}

export function QuestModal() {
  const open = useUiStore((s) => s.questModalOpen);
  const activeId = useUiStore((s) => s.activeQuestId);
  const close = useUiStore((s) => s.closeQuest);
  const openProfile = useUiStore((s) => s.openProfile);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const storedProfile = useAuthStore((s) => s.profile);

  const { data: sections } = useMissionSections();
  const { data: liveProfile } = useProfile();
  const profile = liveProfile ?? storedProfile;
  const claim = useClaimQuest();

  const [ctaClickedAt, setCtaClickedAt] = useState<number | null>(null);
  const [justClaimed, setJustClaimed] = useState(false);

  // Resolve the active quest from the flat list every render — cheap, O(N).
  const quest = useMemo<Quest | undefined>(() => {
    if (!activeId || !sections) return undefined;
    for (const section of sections) {
      const found = section.quests.find((q) => q.id === activeId);
      if (found) return found;
    }
    return undefined;
  }, [activeId, sections]);

  // Reset transient state whenever the modal target changes — including the
  // shared claim mutation, whose error would otherwise follow to the next quest.
  const resetClaim = claim.reset;
  useEffect(() => {
    setCtaClickedAt(null);
    setJustClaimed(false);
    resetClaim();
  }, [activeId, resetClaim]);

  // After a successful claim, briefly show the success state then close.
  useEffect(() => {
    if (!justClaimed) return;
    const id = window.setTimeout(() => close(), 1500);
    return () => window.clearTimeout(id);
  }, [justClaimed, close]);

  const { secondsLeft, hasStarted } = useCooldownTimer(quest, ctaClickedAt);

  if (!quest) return null;

  const titleId = `quest-modal-title-${quest.id}`;

  // No wallet, no quest. Visitors see the connect gate regardless of which
  // quest they tap — claiming requires an authenticated session, period.
  if (!isAuthenticated) {
    return (
      <Modal open={open} onClose={close} labelledBy={titleId} className="w-full max-w-2xl">
        <SignInGate title={quest.title} />
      </Modal>
    );
  }

  // "Connect Telegram Bot" is a client-only onboarding step, not a claimable
  // quest — it always renders as a plain gate (same family as SignInGate /
  // SocialLinkGate) rather than the hero/footer claim view.
  if (quest.type === 'telegram_bot_connect') {
    return (
      <Modal open={open} onClose={close} labelledBy={titleId} className="w-full max-w-2xl">
        {quest.lockedBy ? (
          <SequenceLockedGate quest={quest} onClose={close} />
        ) : (
          <TelegramBotConnectGate quest={quest} />
        )}
      </Modal>
    );
  }

  // Telegram / Discord quests are blocked until the matching social account is
  // linked on the profile. Surface the same connection flow as the profile
  // modal — closing here and re-opening the profile so the user links once.
  // A quest still locked by the onboarding sequence skips this: the locked
  // view below points at the step to do first, not at an account to link.
  const required = quest.requiresSocial;
  if (
    !quest.lockedBy &&
    (required === 'telegram' || required === 'discord' || required === 'x') &&
    !profile?.linkedSocials?.[required]
  ) {
    const handleOpenProfile = () => {
      close();
      openProfile();
    };
    return (
      <Modal
        open={open}
        onClose={close}
        labelledBy={titleId}
        className="w-full max-w-2xl"
      >
        <SocialLinkGate
          title={quest.title}
          provider={required}
          onOpenProfile={handleOpenProfile}
        />
      </Modal>
    );
  }

  const isCompleted = quest.status === 'completed' || justClaimed;
  const isLocked = quest.status === 'locked';
  const isComingSoon = quest.status === 'coming_soon';
  const showRecheckNotice = hasContinuousCheck(quest);

  // Claim-button gating
  const claimDisabled =
    isLocked ||
    isComingSoon ||
    isCompleted ||
    claim.isPending ||
    (quest.verifyMethod === 'manual' && (!hasStarted || (secondsLeft ?? 0) > 0));

  let claimLabel = 'Collect points';
  if (claim.isPending) claimLabel = 'Collecting…';
  else if (isComingSoon) claimLabel = 'Coming soon';
  else if (quest.verifyMethod === 'manual' && !hasStarted) claimLabel = 'Open the link first';

  async function handleClaim() {
    if (!quest) return;
    try {
      await claim.mutateAsync(quest.id);
      setJustClaimed(true);
    } catch {
      // Surfaced inline via claim.error below — nothing else to do.
    }
  }

  function handleCtaClick() {
    setCtaClickedAt(Date.now());
  }

  return (
    <Modal
      open={open}
      onClose={close}
      labelledBy={titleId}
      className="w-full max-w-2xl"
    >
      {/* Hero — image when provided, otherwise a type-specific branded banner. */}
      {quest.heroImage ? (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-paper-warm">
          <img
            src={quest.heroImage}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : (
        <QuestHero quest={quest} />
      )}

      <div className="space-y-5 px-6 py-6">
        {/* Status / points row */}
        <div className="flex flex-wrap items-center gap-2">
          {isCompleted ? (
            <Badge variant="completed" icon={<Check className="h-3 w-3" aria-hidden="true" />}>
              Completed
            </Badge>
          ) : isLocked ? (
            <Badge variant="locked" icon={<Lock className="h-3 w-3" aria-hidden="true" />}>
              Locked
            </Badge>
          ) : isComingSoon ? (
            <Badge variant="soon" icon={<Hourglass className="h-3 w-3" aria-hidden="true" />}>
              Coming soon
            </Badge>
          ) : (
            <Badge variant="available" pulse>
              {STATUS_LABEL[quest.status]}
            </Badge>
          )}
          {quest.points > 0 ? (
            <Badge
              variant="points"
              className={cn(isCompleted && 'opacity-60 line-through decoration-1')}
            >
              {`+${formatPoints(quest.points)} pts`}
            </Badge>
          ) : null}
        </div>

        {/* Title + description */}
        <div className="space-y-2">
          <h2
            id={titleId}
            className="font-display text-2xl font-bold tracking-tightish text-ink-950"
          >
            {quest.title}
          </h2>
          <p className="text-sm leading-relaxed text-slate2-700">{quest.description}</p>
        </div>

        {/* Prerequisites (locked bonus quest): rich checklist when we have the
            resolved list, else the plain-text hint as a fallback. */}
        {quest.prerequisites && quest.prerequisites.length > 0 ? (
          <PrerequisiteChecklist items={quest.prerequisites} />
        ) : quest.requirementHint ? (
          <p className="eyebrow eyebrow-locked w-fit">
            <Lock className="h-3 w-3" aria-hidden="true" />
            {quest.requirementHint}
          </p>
        ) : null}

        {/* Continuous-check notice: persistence-required quests. Same copy
            for every type, kept low-key so it doesn't compete with the CTA. */}
        {showRecheckNotice ? (
          <p
            role="note"
            className="flex items-start gap-2 text-xs leading-relaxed text-slate2-600"
          >
            <AlertTriangle
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate2-400"
              aria-hidden="true"
              strokeWidth={2}
            />
            <span>{CONTINUOUS_CHECK_MESSAGE}</span>
          </p>
        ) : null}

        {/* Instructions */}
        {quest.instructions && quest.instructions.length > 0 ? (
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-ink-900">
            {quest.instructions.map((step, i) => (
              <li key={i} className="leading-relaxed">
                {step}
              </li>
            ))}
          </ol>
        ) : null}

        {/* Footer — success replaces the row for 1.5s before close. */}
        {justClaimed ? (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }}
            className="relative overflow-visible rounded-xl bg-gradient-to-br from-forest-50 via-white to-forest-50 px-4 py-4 ring-1 ring-inset ring-[#CFE6D8]"
          >
            <ConfettiBurst fire={justClaimed} />
            <div className="relative flex items-center justify-center gap-2 text-sm font-semibold text-forest-700">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-forest-500 text-white shadow-md">
                <Check className="h-4 w-4" aria-hidden="true" strokeWidth={2.75} />
              </span>
              <span>Collected</span>
              <span className="font-display text-base font-bold text-forest-700">
                +{formatPoints(quest.points)}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-wide text-forest-600/80">
                pts
              </span>
            </div>
          </motion.div>
        ) : (
          <div className="flex flex-col-reverse items-stretch gap-3 pt-1 sm:flex-row sm:items-center sm:justify-end">
            {quest.ctaUrl && !isLocked && !isComingSoon ? (
              <a
                href={quest.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCtaClick}
                className="btn-ghost"
              >
                {quest.ctaLabel ?? 'Open link'}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" strokeWidth={2.25} />
              </a>
            ) : null}

            <div className="flex flex-col items-end gap-1.5">
              {claim.error ? (
                <span role="alert" className="max-w-xs text-right text-xs text-rose2-600">
                  {claim.error.message}
                </span>
              ) : null}
              <div className="flex items-center justify-end gap-2">
                {quest.verifyMethod === 'manual' &&
                hasStarted &&
                (secondsLeft ?? 0) > 0 ? (
                  <span className="text-xs text-slate2-500">Available in {secondsLeft}s</span>
                ) : null}
                <button
                  type="button"
                  onClick={handleClaim}
                  disabled={claimDisabled}
                  className="btn-water disabled:cursor-not-allowed disabled:opacity-50"
                  aria-live="polite"
                >
                  {isCompleted ? <Check className="h-4 w-4" aria-hidden="true" strokeWidth={2.5} /> : null}
                  {isCompleted ? 'Collected' : claimLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default QuestModal;
